import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Rating,
  Stack,
  Button,
  LinearProgress,
  TextField,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Timer as TimerIcon,
  SmokingRooms as SmokingIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material'
import { SmokingSession, useUpdateSessionMutation } from '@store/api/sessionApi'
import { format } from 'date-fns'
import { useSmokingSessionRuntime } from '@/contexts/SmokingSessionRuntimeContext'
import { formatSmokingTimer } from '@utils/smokingSessionState'
import { trackFeature } from '@utils/trackFeature'

interface ActiveSessionCardProps {
  session: SmokingSession
  onFinished?: () => void
}

const STAGES = [
  { key: 'first', label: 'First Third', fraction: 1 / 3 },
  { key: 'second', label: 'Second Third', fraction: 2 / 3 },
  { key: 'final', label: 'Final Third', fraction: 1 },
] as const

const ActiveSessionCard: React.FC<ActiveSessionCardProps> = ({ session, onFinished }) => {
  const [updateSession, { isLoading }] = useUpdateSessionMutation()
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.only('xs'), { noSsr: true })
  const { elapsedForSession, isSessionPaused, setSessionPaused } = useSmokingSessionRuntime()
  const elapsedSeconds = elapsedForSession(session.id, session.startTime)
  const paused = isSessionPaused(session.id)

  const [activeStage, setActiveStage] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [tastingNotes, setTastingNotes] = useState(session.tastingNotes || '')
  const [rating, setRating] = useState<number | null>(session.rating ?? null)

  const estimatedDuration = (session.durationMinutes || 60) * 60
  const progress = Math.min(100, (elapsedSeconds / estimatedDuration) * 100)

  useEffect(() => {
    const newStage = STAGES.filter(s => elapsedSeconds >= s.fraction * estimatedDuration).length
    if (newStage > activeStage) setActiveStage(newStage)
  }, [elapsedSeconds, estimatedDuration, activeStage])

  const handleMarkStage = (stageIndex: number) => {
    setActiveStage(Math.max(activeStage, stageIndex + 1))
  }

  const handleFinish = async () => {
    const durationMinutes = Math.round(elapsedSeconds / 60)
    try {
      await updateSession({
        id: session.id,
        updates: {
          endTime: new Date().toISOString(),
          durationMinutes,
          isDraft: false,
          ...(tastingNotes && { tastingNotes }),
          ...(rating != null && { rating }),
        }
      }).unwrap()
      trackFeature('cta_session_finish', { item: session.id })
      if (rating != null) {
        trackFeature('cta_session_rate', { item: session.id })
      }
      onFinished?.()
    } catch (error) {
      console.error('Failed to finish session:', error)
    }
  }

  const stageChips = (
    <Stack direction={isXs ? 'column' : 'row'} spacing={1} mb={2} useFlexGap>
      {STAGES.map((stage, i) => (
        <Chip
          key={stage.key}
          label={stage.label}
          color={activeStage > i ? 'success' : 'default'}
          variant={activeStage > i ? 'filled' : 'outlined'}
          onClick={() => handleMarkStage(i)}
          sx={{
            flex: isXs ? undefined : 1,
            width: isXs ? '100%' : undefined,
            fontWeight: activeStage > i ? 'bold' : 'normal',
            minHeight: 44,
          }}
        />
      ))}
    </Stack>
  )

  return (
    <Card sx={{
      border: '2px solid',
      borderColor: 'warning.main',
      boxShadow: (theme) => `0 0 16px ${theme.palette.warning.light}`,
    }}>
      <Box sx={{
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <SmokingIcon />
          <Typography variant="subtitle1" fontWeight="bold">
            Currently Smoking
          </Typography>
        </Box>
        <Typography variant="caption">
          Started {format(new Date(session.startTime || session.smokedDate), 'h:mm a')}
        </Typography>
      </Box>

      <CardContent>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {session.cigar.cigarLine.brand.name} {session.cigar.cigarLine.name}
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={2}>
          {session.cigar.vitola.name}
        </Typography>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <TimerIcon color={paused ? 'warning' : 'primary'} />
          <Typography
            variant="h3"
            fontFamily="monospace"
            fontWeight="bold"
            color={paused ? 'warning.main' : 'primary.main'}
          >
            {formatSmokingTimer(elapsedSeconds)}
          </Typography>
        </Box>

        <Box mb={2}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                bgcolor: progress >= 100 ? 'success.main' : 'warning.main',
              }
            }}
          />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary">
              {Math.round(elapsedSeconds / 60)}m elapsed
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ~{session.durationMinutes || 60}m estimated
            </Typography>
          </Box>
        </Box>

        {stageChips}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={2}>
          {!paused ? (
            <Button
              variant="outlined"
              color="warning"
              size="large"
              fullWidth
              startIcon={<PauseIcon />}
              onClick={() => setSessionPaused(session.id, true)}
              sx={{ minHeight: 48 }}
            >
              Pause
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="success"
              size="large"
              fullWidth
              startIcon={<PlayIcon />}
              onClick={() => setSessionPaused(session.id, false)}
              sx={{ minHeight: 48 }}
            >
              Resume
            </Button>
          )}
          <Button
            variant="contained"
            color="error"
            fullWidth
            size="large"
            startIcon={<StopIcon />}
            onClick={handleFinish}
            disabled={isLoading}
            sx={{ minHeight: 48 }}
          >
            {isLoading ? 'Finishing...' : 'Finish Session'}
          </Button>
        </Stack>

        <Button
          size="small"
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
          sx={{ mb: 1 }}
        >
          {expanded ? 'Hide Notes' : 'Add Notes & Rating'}
        </Button>
        <Collapse in={expanded}>
          <Stack spacing={2} mt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>Rating</Typography>
              <Rating
                value={rating}
                onChange={(_, v) => setRating(v)}
                precision={0.5}
                size="large"
              />
            </Box>
            <TextField
              label="Tasting Notes"
              multiline
              rows={3}
              value={tastingNotes}
              onChange={(e) => setTastingNotes(e.target.value)}
              placeholder="What flavors are you picking up?"
              fullWidth
              size="small"
            />
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export default ActiveSessionCard
